import React, { useState, useEffect, useCallback } from 'react';
import { formatCurrency } from '../../../utils/formatters';
import {
  configService,
  StoreCharge,
  StoreChargeCreate,
  StoreChargeUpdate,
  FeeTriggerOption,
} from '../../../services/configService';
import {
  EffectiveFeeCategory,
  feeCategoryService,
  formatCategoryName,
} from '../../../services/feeCategoryService';

interface FeeFormData {
  item: string;
  amount: number;
  flat: boolean;
  frequency: string;
  fee_category_id: number | null;
  charge_category: string; // Keep for backward compatibility display
  fee_trigger: string;
  active: boolean;
}

const FREQUENCY_OPTIONS = [
  { value: 'once', label: 'One-time (Per Booking)' },
  { value: 'nightly', label: 'Per Night' },
  { value: 'weekly', label: 'Per Week' },
  { value: 'monthly', label: 'Per Month' },
];

const FeesCard: React.FC = () => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [fees, setFees] = useState<StoreCharge[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingFee, setEditingFee] = useState<StoreCharge | null>(null);
  const [feeTriggers, setFeeTriggers] = useState<FeeTriggerOption[]>([]);
  const [feeCategories, setFeeCategories] = useState<EffectiveFeeCategory[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [formData, setFormData] = useState<FeeFormData>({
    item: '',
    amount: 0,
    flat: true,
    frequency: 'once',
    fee_category_id: null,
    charge_category: '',
    fee_trigger: '',
    active: true,
  });
  const [saving, setSaving] = useState(false);

  // Get store_id from localStorage (set during login)
  const getStoreId = (): number | undefined => {
    try {
      const userData = localStorage.getItem('managerUserData');
      if (userData) {
        const parsed = JSON.parse(userData);
        return parsed.store_id;
      }
    } catch {
      return undefined;
    }
    return undefined;
  };

  const storeId = getStoreId();

  const loadFees = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const feeList = await configService.getStoreCharges(storeId);
      setFees(feeList);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load fees');
      console.error('Failed to load fees:', err);
    } finally {
      setLoading(false);
    }
  }, [storeId]);

  const loadFeeTriggers = useCallback(async () => {
    try {
      const triggers = await configService.getFeeTriggers();
      setFeeTriggers(triggers);
    } catch (err) {
      console.error('Failed to load fee triggers:', err);
    }
  }, []);

  const loadFeeCategories = useCallback(async () => {
    if (!storeId) {
      console.warn('FeesCard: No storeId found, cannot load categories');
      setCategoriesLoading(false);
      return;
    }
    try {
      setCategoriesLoading(true);
      console.log('FeesCard: Loading fee categories for storeId:', storeId);
      // Get all active fee categories from store_fee_category_config
      const categories = await feeCategoryService.getEffectiveFeeCategories(storeId, {
        activeOnly: true,
      });
      console.log('FeesCard: Received categories:', categories);
      // Show all categories (no filtering) so managers can assign any category to fees
      setFeeCategories(categories);
    } catch (err) {
      console.error('Failed to load fee categories:', err);
    } finally {
      setCategoriesLoading(false);
    }
  }, [storeId]);

  // Fetch fees, triggers, and categories on mount
  useEffect(() => {
    loadFees();
    loadFeeTriggers();
    loadFeeCategories();
  }, [loadFees, loadFeeTriggers, loadFeeCategories]);

  const handleDelete = async (feeId: number, feeName: string) => {
    if (!window.confirm(`Are you sure you want to delete "${feeName}"? This cannot be undone.`)) {
      return;
    }
    try {
      await configService.deleteStoreCharge(feeId);
      setFees(fees.filter(fee => fee.id !== feeId));
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete fee');
    }
  };

  const openAddModal = () => {
    setEditingFee(null);
    setFormData({
      item: '',
      amount: 0,
      flat: true,
      frequency: 'once',
      fee_category_id: null,
      charge_category: '',
      fee_trigger: '',
      active: true,
    });
    setShowModal(true);
  };

  const openEditModal = (fee: StoreCharge) => {
    setEditingFee(fee);
    setFormData({
      item: fee.item,
      amount: fee.amount,
      flat: fee.flat,
      frequency: fee.frequency,
      fee_category_id: fee.fee_category_id || null,
      charge_category: fee.charge_category || '',
      fee_trigger: fee.fee_trigger || '',
      active: fee.active,
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.item.trim()) {
      alert('Please enter a fee name');
      return;
    }
    if (formData.amount <= 0) {
      alert('Please enter a valid amount');
      return;
    }
    if (!formData.fee_category_id) {
      alert('Please select a fee category');
      return;
    }

    // Get category name for backward compatibility
    const selectedCategory = feeCategories.find(c => c.id === formData.fee_category_id);
    const categoryName = selectedCategory?.category_name || '';

    setSaving(true);
    try {
      if (editingFee) {
        // Update existing fee
        const updateData: StoreChargeUpdate = {
          item: formData.item,
          amount: formData.amount,
          flat: formData.flat,
          frequency: formData.frequency,
          fee_category_id: formData.fee_category_id,
          charge_category: categoryName, // Keep for backward compat
          fee_trigger: formData.fee_trigger || undefined,
          active: formData.active,
        };
        const updated = await configService.updateStoreCharge(editingFee.id, updateData);
        setFees(fees.map(f => f.id === editingFee.id ? updated : f));
      } else {
        // Create new fee
        const createData: StoreChargeCreate = {
          item: formData.item,
          amount: formData.amount,
          flat: formData.flat,
          frequency: formData.frequency,
          fee_category_id: formData.fee_category_id,
          charge_category: categoryName, // Keep for backward compat
          fee_trigger: formData.fee_trigger || undefined,
          active: formData.active,
        };
        const created = await configService.createStoreCharge(createData, storeId);
        setFees([...fees, created]);
      }
      setShowModal(false);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to save fee');
    } finally {
      setSaving(false);
    }
  };

  const formatAmountDisplay = (fee: StoreCharge) => {
    if (fee.flat) {
      return formatCurrency(fee.amount);
    }
    return `${fee.amount}%`;
  };

  const getFrequencyLabel = (frequency: string) => {
    const option = FREQUENCY_OPTIONS.find(f => f.value === frequency);
    return option ? option.label : frequency;
  };

  const getCategoryLabel = (fee: StoreCharge) => {
    // First try to find by fee_category_id
    if (fee.fee_category_id) {
      const category = feeCategories.find(c => c.id === fee.fee_category_id);
      if (category) {
        return formatCategoryName(category.category_name);
      }
    }
    // Fallback to charge_category string (legacy)
    if (fee.charge_category) {
      return formatCategoryName(fee.charge_category);
    }
    return 'Unknown';
  };

  return (
    <div className="config-card">
      <div
        className="config-card-header"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="config-card-title">
          <span className="config-card-icon">F</span>
          <h2>Fees</h2>
        </div>
        <div className="config-card-header-right">
          <span className="config-card-db-label">campground_db/store_imposed_charges</span>
          <span className={`config-card-toggle ${isExpanded ? 'expanded' : ''}`}>
            {String.fromCharCode(9660)}
          </span>
        </div>
      </div>

      {isExpanded && (
        <div className="config-card-content">
          {loading ? (
            <div className="loading-state">Loading fees...</div>
          ) : error ? (
            <div className="error-state">
              <p>{error}</p>
              <button className="btn btn-primary btn-sm" onClick={loadFees}>
                Retry
              </button>
            </div>
          ) : fees.length === 0 ? (
            <div className="empty-state">
              <p>No fees configured yet.</p>
            </div>
          ) : (
            <table className="config-table">
              <thead>
                <tr>
                  <th>Fee Name</th>
                  <th>Amount</th>
                  <th>Frequency</th>
                  <th>Category</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {fees.map(fee => (
                  <tr key={fee.id}>
                    <td>{fee.item}</td>
                    <td className="amount">{formatAmountDisplay(fee)}</td>
                    <td>{getFrequencyLabel(fee.frequency)}</td>
                    <td>{getCategoryLabel(fee)}</td>
                    <td className={fee.active ? 'status-active' : 'status-inactive'}>
                      {fee.active ? 'Active' : 'Inactive'}
                    </td>
                    <td>
                      <div className="config-actions">
                        <button
                          className="btn btn-text btn-sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            openEditModal(fee);
                          }}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-text btn-sm btn-danger"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(fee.id, fee.item);
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="config-card-footer">
            <button className="btn btn-primary btn-sm" onClick={openAddModal}>
              + Add Fee
            </button>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingFee ? 'Edit Fee' : 'Add Fee'}</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>
                {String.fromCharCode(215)}
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Fee Name *</label>
                <input
                  type="text"
                  value={formData.item}
                  onChange={(e) => setFormData({ ...formData, item: e.target.value })}
                  placeholder="e.g., Booking Fee, Pet Fee"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Amount Type *</label>
                  <select
                    value={formData.flat ? 'flat' : 'percentage'}
                    onChange={(e) => setFormData({ ...formData, flat: e.target.value === 'flat' })}
                  >
                    <option value="flat">Flat Amount ($)</option>
                    <option value="percentage">Percentage (%)</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Amount *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.amount || ''}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value === '' ? 0 : parseFloat(e.target.value) })}
                    placeholder={formData.flat ? '10.00' : '5'}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Frequency *</label>
                  <select
                    value={formData.frequency}
                    onChange={(e) => setFormData({ ...formData, frequency: e.target.value })}
                  >
                    {FREQUENCY_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Category *</label>
                  <select
                    value={formData.fee_category_id || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      fee_category_id: e.target.value ? parseInt(e.target.value, 10) : null
                    })}
                    disabled={categoriesLoading}
                  >
                    <option value="">-- Select Category --</option>
                    {feeCategories.map(cat => (
                      <option key={cat.id} value={cat.id}>
                        {formatCategoryName(cat.category_name)}
                        {cat.is_taxable ? ' (Taxable)' : ''}
                      </option>
                    ))}
                  </select>
                  {categoriesLoading && <small className="form-hint">Loading categories...</small>}
                </div>
              </div>
              {feeTriggers.length > 0 && (
                <div className="form-group">
                  <label>Fee Trigger</label>
                  <select
                    value={formData.fee_trigger}
                    onChange={(e) => setFormData({ ...formData, fee_trigger: e.target.value })}
                  >
                    {feeTriggers.map(trigger => (
                      <option key={trigger.value} value={trigger.value}>
                        {trigger.label}
                      </option>
                    ))}
                  </select>
                  <small className="form-hint">When should this fee be applied?</small>
                </div>
              )}
              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.active}
                    onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                  />
                  Active
                </label>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowModal(false)}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeesCard;
